import { useRef } from "react";
import { messages } from "../i18n/index.ts";
import { playSound } from "../lib/sound.ts";
import { Sheet } from "./Sheet.tsx";

interface SoundSettingsSheetProps {
  open: boolean;
  volume: number;
  onVolumeChange: (volume: number) => void;
  onToggleMuted: () => void;
  onClose: () => void;
}

export function SoundSettingsSheet({
  open,
  volume,
  onVolumeChange,
  onToggleMuted,
  onClose,
}: SoundSettingsSheetProps) {
  const lastPreviewAt = useRef(0);

  function changeVolume(next: number) {
    onVolumeChange(next);
    const now = performance.now();
    if (next > 0 && now - lastPreviewAt.current >= 140) {
      lastPreviewAt.current = now;
      playSound("letter", { userGesture: true });
    }
  }

  function toggleMuted() {
    const willEnable = volume === 0;
    onToggleMuted();
    if (willEnable) playSound("ui-click", { userGesture: true });
  }

  return (
    <Sheet
      open={open}
      title={messages.sound.title}
      accent="mint"
      placement="center"
      onClose={onClose}
    >
      <div className="sound-settings">
        <p className="sound-settings__description">{messages.sound.description}</p>

        <div className="sound-settings__level">
          <span>{messages.sound.volume}</span>
          <strong>{messages.sound.percent(volume)}</strong>
        </div>
        <div className="sound-settings__slider-row">
          <span aria-hidden="true">−</span>
          <input
            className="sound-settings__slider"
            type="range"
            min="0"
            max="100"
            step="5"
            value={volume}
            aria-label={messages.sound.volume}
            aria-valuetext={messages.sound.percent(volume)}
            style={{ ["--sound-level" as string]: `${volume}%` }}
            onChange={(event) => changeVolume(Number(event.currentTarget.value))}
          />
          <span aria-hidden="true">+</span>
        </div>

        <div className="sound-settings__actions">
          <button
            type="button"
            className="btn-secondary"
            data-sound-control
            onClick={toggleMuted}
          >
            {volume === 0 ? messages.sound.unmute : messages.sound.mute}
          </button>
          <button
            type="button"
            className="btn-primary"
            data-sound-control
            disabled={volume === 0}
            onClick={() => playSound("notification", { userGesture: true })}
          >
            {messages.sound.preview}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
